import { useEffect, useState, useMemo, useRef } from "react";
import ReportFilters from "../component/reports/reportFilter";
import { studioApi } from "../api";
import { Button, Card, Checkbox, Input, message, Table } from "antd";
import {
  ALL_COLUMNS,
  DEFAULT_COLUMNS,
  REQUIRED_COLUMNS,
} from "../component/reports/constants";
import ColumnSelectorModal from "../component/reports/columnSelectorModal";
import { ExportOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { formatDateTime } from "../utils/helpers";

export interface IReportQuery {
  startDate?: string;
  endDate?: string;
  classStatus?: "cancelled" | "reschedule" | "ontime" | "delay";
  course?: string;
  lesson?: string;
  batch?: string;
  teacher?: string;
  center?:string;
}

interface Option {
  label: string;
  value: string;
}

function Report() {
  const [lessons, setLessons] = useState<Option[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<any[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [query, setQuery] = useState<IReportQuery>({
    startDate: undefined,
    endDate: undefined,
  });

  const fetchLessons = async (courseId: string) => {
    if (!courseId) {
      setLessons([]);
      return;
    }
    try {
      const { data } = await studioApi.get<{ id: number; name: string }[]>(
        `/cms/lessons/?course_id=${courseId}`
      );
      const formattedLessons = data.map((lesson) => ({
        label: `${lesson.id} - ${lesson.name}`,
        value: String(lesson.id),
      }));
      setLessons(formattedLessons);
    } catch (err) {
      message.error("Failed to fetch lessons");
      setLessons([]);
    }
  };

  const [teachers, setTeachers] = useState<Option[]>([]);
  const getTeachers = async () => {
    try {
      const { data } = await studioApi.get<any[]>("/cms/teachers");
      const formattedTeachers = data.map((teacher) => ({
        label: teacher.name,
        value: String(teacher.id),
      }));
      setTeachers(formattedTeachers);
    } catch {
      message.error("Failed to fetch teachers");
      setTeachers([]);
    }
  };
  const [centers, setCenters] = useState<Option[]>([]);
  const getCenters = async () => {
    try {
      const { data } = await studioApi.get<any[]>("/cms/centers");
      const formattedcenters = data.map((center) => ({
        label: center.name,
        value: String(center.id),
      }));
      setCenters(formattedcenters);
    } catch {
      message.error("Failed to fetch centers");
      setCenters([]);
    }
  };

  const [batches, setBatches] = useState<Option[]>([]);
  const getBatches = async () => {
    try {
      const { data } = await studioApi.get<any[]>("/cms/batches");
      const formattedBatches = data.map((batch) => ({
        label: batch.name,
        value: String(batch.id),
      }));
      setBatches(formattedBatches);
    } catch {
      message.error("Failed to fetch batches");
      setBatches([]);
    }
  };

  const [courses, setCourses] = useState<Option[]>([]);
  const getCourses = async (batchId?: string) => {
    try {
      const { data } = await studioApi.get<any[]>("/cms/courses", {
        params: batchId ? { batch_id: batchId } : {},
      });
      const formattedCourses = data.map((course) => ({
        label: course.title,
        value: String(course.id),
      }));
      setCourses(formattedCourses);
    } catch {
      message.error("Failed to fetch courses");
      setCourses([]);
    }
  };

  const updateQuery = (payload: Partial<IReportQuery>) => {
    setQuery((prev) => {
      const nextQuery = { ...prev, ...payload };

      if ("course" in payload) {
        fetchLessons(payload.course || "");
      }

      if ("batch" in payload) {
        getCourses(payload.batch || "");
        nextQuery.course = undefined;
        nextQuery.lesson = undefined;
        setLessons([]);
      }

      return nextQuery;
    });
  };

  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const STORAGE_KEY = "report_selected_columns";
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return Array.from(new Set([...REQUIRED_COLUMNS, ...parsed]));
    }
    return DEFAULT_COLUMNS;
  });

  const getVideoIdByVid = async (vid: string) => {
    const { data } = await studioApi.get<{ id: string }>("/videos/video-id", {
      params: { vid },
    });
  
    return data;
  };

 

const handleClassDetailsClick = async (record: any) => {
  try {
    const result = await getVideoIdByVid(record.vid);

    if (result?.id) {
      window.open(`/studio/${result.id}`, "_blank");
      return;
    }

    message.error("Video not found");
  } catch (error) {
    console.error("Failed to get video ID:", error);
    message.error("Failed to open class details");
  }
};
  const FIXED_COLUMNS = ["web", "youtube", "kgs-studio"];
  const CHECKBOX_COLUMN_WIDTH = 80;

  const serialNumberColumn = {
    title: "S.No",
    key: "serialNumber",
    width: 70,
    fixed: "left" as const,
    render: (_: any, __: any, index: number) => index + 1,
  };

  const columns = [
    serialNumberColumn,
    ...ALL_COLUMNS.filter((column) => selectedColumns.includes(column.key)).map(
      (column) => {
        let columnWidth = 180;
        if (FIXED_COLUMNS.includes(column.key)) {
          columnWidth = CHECKBOX_COLUMN_WIDTH;
        } else if (column.key === "courseId") {
          columnWidth = 100;
        } else if (column.key === "vid") {
          columnWidth = 90;
        }
  
        return {
          title: column.title,
          dataIndex: column.key,
          key: column.key,
          fixed:
            isEditing && FIXED_COLUMNS.includes(column.key)
              ? ("right" as const)
              : undefined,
          width: columnWidth,
          render: (value: any, record: any) => {
            if (FIXED_COLUMNS.includes(column.key)) {
              return (
                <Checkbox
                  checked={value}
                  disabled={!isEditing}
                  onChange={(e) =>
                    handleFieldChange(
                      record.key,
                      column.key,
                      e.target.checked
                    )
                  }
                />
              );
            }
            if (column.key === "remark" && isEditing) {
              return (
                <Input
                  value={value}
                  onChange={(e) =>
                    handleFieldChange(record.key, "remark", e.target.value)
                  }
                  placeholder="Enter remark..."
                />
              );
            }
            if (column.key === "classDetails") {
              return (
                <span
                  onClick={() => handleClassDetailsClick(record)}
                  className="cursor-pointer "
                >
                  {value}
                </span>
              );
            }
  
            return value;
          },
        };
      }
    ),
  ];
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [exporting, setExporting] = useState(false);
  const getLiveTracking = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const params: any = { page, limit };
      if (query.startDate) params.startDate = query.startDate;
      if (query.endDate) params.endDate = query.endDate;
      if (query.classStatus) params.classStatus = query.classStatus;
      if (query.course) params.course = query.course;
      if (query.lesson) params.lesson = query.lesson;
      if (query.batch) params.batch = query.batch;
      if (query.teacher) params.teacher = query.teacher;
      if(query.center) params.center= query.center;

      const response = await studioApi.get("/cms/videos/live-tracking", {
        params,
        signal,
      });

      setData(response.data.results || []);
      setTotal(response.data.total || 0);
    } catch (error: any) {
      if (error?.code !== "ERR_CANCELED") {
        message.error("Failed to fetch report data");
        setData([]);
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const exportReport = async () => {
    try {
      setExporting(true);

      const params: any = {};
      if (query.startDate) params.startDate = query.startDate;
      if (query.endDate) params.endDate = query.endDate;
      if (query.classStatus) params.classStatus = query.classStatus;
      if (query.course) params.course = query.course;
      if (query.lesson) params.lesson = query.lesson;
      if (query.batch) params.batch = query.batch;
      if (query.teacher) params.teacher = query.teacher;

      const response = await studioApi.get("/cms/videos/live-tracking/export", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `live-tracking-${dayjs().format("YYYY-MM-DD-HH-mm")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      let errorMessage = "Failed to export report";

      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          if (errorJson?.message) {
            errorMessage = errorJson.message;
          }
        } catch {}
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      message.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };
  const tableData = useMemo(() => {
    return data.map((item) => {
      const details = item.details;
      const classStatus = details?.class_status;

      let status = "";
      let statusBy = "";
      let statusMessage = "";

      if (classStatus === "delay") {
        status = `Delayed by ${details?.delay || 0} mins`;
        statusBy = details?.delay_by ? ` ${details.delayed_by_name}` : "";
        statusMessage = details?.delay_msg || "";
      } else if (classStatus === "reschedule") {
        status = "Rescheduled";
        statusBy = details?.re_schedule_by
          ? `${details.rescheduleUser.first_name}`
          : "";
        statusMessage = details?.re_schedule_msg || "";
      } else if (classStatus === "cancelled") {
        status = "Cancelled";
        statusBy = details?.cancel_by
          ? ` ${details.cancelUser.first_name}`
          : "";
        statusMessage = details?.cancel_msg || "";
      } else if (classStatus === "ontime") {
        status = "On Time";
      }

      return {
        key: item.id,
        vid: item?.id,
        courseId: item.course?.id,
        classDetails: item.name,
        scheduledTeacher: item.teacher?.name,
        status,
        statusBy,
        statusMessage,
        startAt: formatDateTime(details?.actual_start_time),
        startBy: details?.start_by_name,
        abbr: item.course?.abbr,
        from: formatDateTime(item?.published_at),
        to: formatDateTime(details?.actual_end_time) ,
        lesson: item.lesson?.name ?? "-",
        teacherId: item.teacher?.id,
        teacherName: item.teacher?.name,
        location: item.center?.abbr,
        remark:details?.remark,
        web: item.type === "public",
        youtube: item.type === "yt" || item.type === "kgs",
        "kgs-studio": item.type === "kgs",
      };
    });
  }, [data]);


  const handleFieldChange = (
    rowKey: number,
    field: string,
    value: any
  ) => {
    setEditedData((prev) =>
      prev.map((row) =>
        row.key === rowKey
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  const transformAuditPayload = (editedRows: any[], originalData: any[]) => {
    return editedRows
      .filter((row) => {
        if (row.vid === null || row.vid === undefined) return false;
        const originalItem = originalData.find((item) => item.id === row.key);
        if (!originalItem) return false;
  
        const hasWebChanged = row.web !== (originalItem.type === "public");
        const hasYoutubeChanged =
          row.youtube !== (originalItem.type === "yt" || originalItem.type === "kgs");
        const hasKgsChanged =
          row["kgs-studio"] !== (originalItem.type === "kgs");
        const hasRemarkChanged =
          (row.remark || "") !== (originalItem.details?.remark || "");
  
        return (
          hasWebChanged ||
          hasYoutubeChanged ||
          hasKgsChanged ||
          hasRemarkChanged
        );
      })
      .map((row) => {
        const originalItem = originalData.find((item) => item.id === row.key);
        let rawStatus = originalItem?.details?.class_status || "ontime";
  
        return {
          status: rawStatus,
          videoId: Number(row.vid),
          kgs: row["kgs-studio"] ? ("TRUE" as const) : ("FALSE" as const),
          yt: row.youtube ? ("TRUE" as const) : ("FALSE" as const),
          pub: row.web ? ("TRUE" as const) : ("FALSE" as const),
          reason: row.statusMessage || undefined,
          actionBy: row.statusBy || undefined,
          remark: row.remark ?? "",
        };
      });
  };

  const queryDepsString = JSON.stringify(query);

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    getLiveTracking(controller.signal);
    return () => {
      controller.abort();
    };
  }, [queryDepsString, page, limit]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedColumns));
  }, [selectedColumns]);

  useEffect(() => {
    getTeachers();
    getCourses();
    getBatches();
    getCenters();
  }, []);

  useEffect(() => {
    setEditedData(tableData);
  }, [tableData]);

  return (
    <Card
    type="inner"
    className="h-[calc(100vh-40px)] flex flex-col overflow-hidden"
    styles={{
      body: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        padding: "16px",
      },
    }}
  >
    <ColumnSelectorModal
      open={columnModalOpen}
      onClose={() => setColumnModalOpen(false)}
      selectedColumns={selectedColumns}
      setSelectedColumns={setSelectedColumns}
    />
    <div className="flex-shrink-0 space-y-3 bg-white z-20 ">
      <div className="flex items-center justify-between w-full">
        <span className="text-lg font-semibold">Report</span>
        <div style={{ display: "flex", gap: 8 }}>
          {!isEditing ? (
            <>
              <Button type="primary" onClick={() => setIsEditing(true)}>
                Audit
              </Button>
              <Button
                type="text"
                icon={<ExportOutlined />}
                loading={exporting}
                disabled={exporting}
                onClick={exportReport}
                className="bg-blue-100 border-blue-500 text-blue-500 hover:!text-blue-600 hover:!bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <Button
                type="text"
                className="bg-orange-100 border-orange-500 text-orange-500 hover:!text-orange-600 hover:!bg-orange-100"
                icon={<SettingOutlined />}
                onClick={() => setColumnModalOpen(true)}
              />
            </>
          ) : (
            <>
              <Button
                type="primary"
                loading={loading}
                onClick={async () => {
                  try {
                    setLoading(true);
                    const payload = transformAuditPayload(editedData, data);
                    await studioApi.post(
                      "/cms/videos/live-tracking/audit",
                      payload
                    );
                    message.success("Changes saved successfully");
                    setIsEditing(false);
                    getLiveTracking();
                  } catch (error) {
                    message.error("Failed to save changes");
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Save
              </Button>
              <Button
                onClick={() => {
                  setEditedData(tableData);
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
      <ReportFilters
        query={query}
        setQuery={updateQuery}
        courses={courses}
        lessons={lessons}
        teachers={teachers}
        batches={batches}
        centers={centers}
      />
    </div>
  
  
    <div className="flex-1">
      <Table
        columns={columns}
        dataSource={editedData}
        scroll={{ x: 2500, y: "calc(100vh - 260px)" }}
        loading={loading}
        rowClassName={(record) => {
          const status = record.status?.toLowerCase();
          if (status === "on time") return "row-ontime";
          if (status === "cancelled") return "row-cancelled";
          if (status === "rescheduled") return "row-reschedule";
          if (status?.includes("delayed")) return "row-delay";
          return "";
        }}
        pagination={{
          current: page,
          pageSize: limit,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (currentPage, pageSize) => {
            setPage(currentPage);
            setLimit(pageSize);
          },
        }}
      />
    </div>
  </Card>
  );
}

export default Report;
